import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { ConfirmProvider } from '@kolectra/ui';
import { AuthProvider, useAuth } from './auth.js';
import { LanguageProvider } from './language.js';
import { LoginPage } from './routes/login.js';
import { ProjectUserApp } from './project-shell.js';
import { projectHasWebSurface } from './project-surface.js';
import { queryClient } from './query-client.js';
import { router } from './router.js';

/**
 * Kolectra Web — the browser client for the deskwork stages: Challenge Creation and
 * Judgement, plus team administration. Talks to the live API; the design system
 * is shared (@kolectra/shared/styles.css). The QueryClient wraps everything (so the
 * login form and refresh can use it); the auth gate decides login vs. app.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AuthGate() {
  const { status, session } = useAuth();

  if (status === 'loading') {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
        <Loader2 size={26} strokeWidth={2} className="spin" style={{ color: 'var(--ink-faint)' }} />
      </div>
    );
  }

  if (status === 'signedOut' || !session) return <LoginPage />;

  // Teamless PROJECT users run in the project shell, which carries whatever project web
  // surfaces they hold and a nav to switch between them: the project-wide overview
  // (can_view_project_reports) and the team-app work surfaces (Judgement etc., via
  // synthesized teams scoped to their assigned languages). Anyone with no web surface
  // (e.g. a Question Recorder, who works in the VoiceKolect mobile app) gets no-access.
  if (session.teams.length === 0) {
    if (!projectHasWebSurface(session)) return <NoAccess hasProject={!!session.project} />;
    return <ProjectUserApp session={session} />;
  }

  return (
    <LanguageProvider>
      <ConfirmProvider>
        <RouterProvider router={router} />
      </ConfirmProvider>
    </LanguageProvider>
  );
}

function NoAccess({ hasProject = false }: { hasProject?: boolean }) {
  const { signOut } = useAuth();
  return (
    <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
      <div className="zone meta" style={{ maxWidth: 420, textAlign: 'center', padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--disp)', fontWeight: 500, fontSize: 18, marginBottom: 6 }}>
          {hasProject ? 'No web surface for your role' : 'No access yet'}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 16 }}>
          {hasProject
            ? 'Your role has no surface on the web dashboard. Question recorders work in the VoiceKolect mobile app.'
            : 'Your account isn’t on a team and has no project access. Ask an admin to add you.'}
        </p>
        <button className="btn" onClick={() => void signOut()}>Sign out</button>
      </div>
    </div>
  );
}
